import { PontCore } from '@/services/pontCore';
import { configureApiAuth } from '@verifime/api-definition';
import { usePasswordless, useTokens } from '@verifime/cognito-passwordless-auth';
import { LoadingScreen } from '@verifime/components';
import { responseInterceptor } from '@verifime/utils';
import { UserProvider } from '@verifime/views';
import { useRouter } from 'next/router';
import React from 'react';

/**
 * AuthenticatedContainer ensures that only authenticated users can access the
 * components wrapped within it.
 *
 * For a detailed explanation of the different authentication patterns used
 * across the portals, see the central documentation here:
 * /docs/authentication-patterns.md
 */
export default function AuthenticatedContainer({ children }: { children: React.ReactElement }) {
  const { tokensParsed, signInStatus } = usePasswordless();
  const { getTokens, getTokensParsed } = useTokens();
  const router = useRouter();

  React.useEffect(() => {
    const validateTokensParsed = (tokensParsedToValidate: typeof tokensParsed) =>
      tokensParsedToValidate && new Date() < tokensParsedToValidate.expireAt;

    if (!validateTokensParsed(tokensParsed)) {
      getTokensParsed().then((newTokensParsed) => {
        if (!validateTokensParsed(newTokensParsed)) {
          const currentPath = router.pathname;
          if (currentPath !== '/') {
            router.push({
              pathname: '/',
              query: { from: encodeURIComponent(router.asPath) },
            });
          }
        }
      });
    }
  }, [getTokensParsed, router, tokensParsed]);

  PontCore.useFetch(async (url, options) => {
    const { accessToken: token } = (await getTokens()) || {};
    return await responseInterceptor(url, token, options);
  });

  // Configure token getter synchronously during render so it is in place
  // before any child component effects fire (child effects run before parent effects).
  configureApiAuth(async () => getTokens().then(({ accessToken }) => accessToken));

  // Never render the protected page until the session is confirmed signed in:
  // the redirect above is asynchronous, so rendering children unconditionally
  // would flash gated content at unauthenticated visitors before it fires.
  // (Unlike client-portal/customer-portal, admin-portal only wraps pages that
  // opt in via `Component.requiresAuthentication`, so no public route is ever
  // rendered through this container and no path-based carve-out is needed.)
  // A usable session means SIGNED_IN, or REFRESHING_SIGN_IN (an established
  // session silently refreshing an expired token). Keep the page mounted for
  // both, so a background refresh does not tear down in-progress page state.
  // Every other status (CHECKING, SIGNING_IN, NOT_SIGNED_IN, SIGNING_OUT) means
  // no usable session yet, so show the loader instead of flashing gated content.
  const hasSession = signInStatus === 'SIGNED_IN' || signInStatus === 'REFRESHING_SIGN_IN';
  if (!hasSession) {
    return (
      <>
        <UserProvider tokens={tokensParsed}>
          <LoadingScreen data-testid="auth-gate-loading" />
        </UserProvider>
      </>
    );
  }

  return (
    <>
      <UserProvider tokens={tokensParsed}>{children}</UserProvider>
    </>
  );
}
